import { CloseOutlined, PlusCircleFilled } from '@ant-design/icons'
import { Collapse, CollapseProps, Input, Popconfirm, Spin, message } from 'antd'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { displayMessage } from '../../../../helpers/Message'
import {
  addPatientPrescription,
  deletePatientPrescription,
  errorPatientPrescription,
  getPatientPrescriptions,
  selectErrorInPatientPrescription,
  selectPatientPrescriptionsById,
  selectSuccessInPatientPrescription,
  successPatientPrescription,
} from '../../../../state/patientPrescription/patientPrescriptionReducer'
import { Prescription } from '../../../../state/patientPrescription/types'
import { TabEmptyText } from '../../PatientsViewStyle'
import { usePatientTabContext } from '../../PatientTabs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

type PatientPrescriptionTabProps = {
  patientKey: string
}

const PrescriptionCollapseTitleDiv = styled.div`
  font-weight: 500;
  font-size: 1.1em;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-left: 10px;
  height: 40px;
  & .endContainer {
    font-size: 10px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    height: 100%;
  }
  & button {
    padding: 2px;
    margin: 0px;
    display: flex;
    background: transparent;
    border: none;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    right: -10px;
    top: -10px;
  }
`

const StyledPrescriptionInput = styled(Input)`
  font-weight: 500;
  font-size: 1.1em;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 65px;
  width: auto;
  margin: 0px 4px;
  & button {
    padding: 2px;
    margin: 0px;
    display: flex;
    background: transparent;
    border: none;
    align-items: center;
    justify-content: center;
  }
`
const StyledPrescriptionTextArea = styled(Input.TextArea)`
  font-weight: 500;
  font-size: 1.1em;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 65px;
  width: 100%;
  margin: 0px 4px;
  padding-right: 50px;
`

const StyledPrescriptionTextAreaContainer = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  padding-bottom: 10px;
  & button {
    padding: 2px;
    position: absolute;
    margin: 0px;
    display: flex;
    right: 20px;
    background: transparent;
    border: none;
    align-items: center;
    justify-content: center;
  }
`

const StyledPrescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const PrescriptionCollapseContentDiv = styled.div`
  padding: 5px 35px;
`

const prescriptionPanelStyle: React.CSSProperties = {
  marginBottom: 12,
  border: 'none',
  borderRadius: 10,
  marginLeft: 3,
  marginRight: 3,
  boxShadow:
    'rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px',
}

const buttonIconSize = {
  small: 20,
  middle: 40,
  large: 60,
}

function PatientPrescriptionTab({ patientKey }: PatientPrescriptionTabProps) {
  const dispatch = useDispatch()
  const [activeCollapse, setActiveCollapse] = useState('')
  const [prescriptionInput, setPrescriptionInput] = useState('')
  const [messageApi, contextHolder] = message.useMessage()
  const patientPrescriptionError = useSelector(selectErrorInPatientPrescription)
  const patientPrescriptionSuccess = useSelector(
    selectSuccessInPatientPrescription
  )
  const { size } = usePatientTabContext()

  useEffect(() => {
    dispatch(getPatientPrescriptions(patientKey))
  }, [dispatch])

  const patientPrescriptionData = useSelector(
    selectPatientPrescriptionsById(patientKey)
  )!
  const patientPrescriptions = patientPrescriptionData?.prescriptions || []

  function PrescriptionCollapseTitle({
    prescription,
    activeCollapse,
  }: {
    prescription: Prescription
    activeCollapse: string
  }) {
    const title =
      prescription.prescriptionText.length < 20
        ? prescription.prescriptionText
        : prescription.prescriptionText.slice(0, 20) + '...'

    const time = dayjs.utc(prescription.prescriptionDate)
    const prescriptionCreateTime = time
      .local()
      .toDate()
      .toLocaleString('en-AU', {
        hour12: false,
      })
    return (
      <PrescriptionCollapseTitleDiv>
        <div>{title}</div>
        <div className='endContainer'>
          <Popconfirm
            title='Delete the Clinical Note'
            description='Are you sure to delete this?'
            onConfirm={(e) => {
              e?.stopPropagation()
              dispatch(
                deletePatientPrescription({
                  patientKey: patientKey,
                  prescriptionId: prescription.prescriptionId,
                })
              )
            }}
            onCancel={(e) => {
              e?.stopPropagation()
            }}
            okText='Yes'
            cancelText='No'
          >
            <button onClick={(e) => e.stopPropagation()}>
              <CloseOutlined />
            </button>
          </Popconfirm>
          {/* {prescription.prescriptionDate} */}
          {prescriptionCreateTime}
        </div>
      </PrescriptionCollapseTitleDiv>
    )
  }

  const patientPrescriptionItems: CollapseProps['items'] =
    patientPrescriptions.map((patientPrescription) => {
      let prescriptionText = patientPrescription.prescriptionText
      prescriptionText = prescriptionText.replace(/\n/g, '<br/>')

      return {
        key: patientPrescription.prescriptionId,
        label: (
          <PrescriptionCollapseTitle
            activeCollapse={activeCollapse}
            prescription={patientPrescription}
          />
        ),
        children: (
          <PrescriptionCollapseContentDiv
            dangerouslySetInnerHTML={{ __html: prescriptionText }}
          ></PrescriptionCollapseContentDiv>
        ),
        style: prescriptionPanelStyle,
      }
    })

  function InputEvent() {
    dispatch(
      addPatientPrescription({
        patientKey: patientKey,
        prescriptionText: prescriptionInput,
      })
    )
    setPrescriptionInput('')
  }

  useEffect(() => {
    if (patientPrescriptionError) {
      displayMessage(patientPrescriptionError, 'error', messageApi)
      dispatch(errorPatientPrescription(null))
    }
    if (patientPrescriptionSuccess) {
      displayMessage(patientPrescriptionSuccess, 'success', messageApi)
      dispatch(successPatientPrescription(null))
    }
  }, [patientPrescriptionError, patientPrescriptionSuccess])

  return (
    <StyledPrescriptionContainer>
      {contextHolder}
      <StyledPrescriptionTextAreaContainer>
        <StyledPrescriptionTextArea
          onChange={(event) => {
            setPrescriptionInput(event.target.value)
          }}
          size={size}
          placeholder='Type New Clinical Note here...'
          value={prescriptionInput}
          maxLength={200}
        />
        <button onClick={InputEvent}>
          <PlusCircleFilled
            style={{
              fontSize: `${buttonIconSize[size]}px`,
              color: 'rgba(0,0,0,.45)',
            }}
          />
        </button>
      </StyledPrescriptionTextAreaContainer>
      {!patientPrescriptionData ? (
        <Spin size='large' />
      ) : (
        <>
          {patientPrescriptionItems.length > 0 ? (
            <Collapse
              size={size}
              onChange={(ids) => {
                setActiveCollapse(ids[0])
              }}
              accordion
              bordered={false}
              className='prescriptionAccordion'
              items={patientPrescriptionItems}
            />
          ) : (
            <TabEmptyText>No Clinical Notes Data</TabEmptyText>
          )}
        </>
      )}
    </StyledPrescriptionContainer>
  )
}

export default PatientPrescriptionTab
